import type { WebCatalog } from '@shared/i18n/web-catalog-type.js';

/** Namespace — верхний ключ веб-каталога, он же значение `ns` у GET /api/v1/i18n/bundle. */
export type Namespace = keyof WebCatalog & string;

/**
 * Каталог локали, разложенный по страницам.
 *
 * ⚠️ Зачем это вообще нужно. Клиент грузил ВЕСЬ каталог перед первым кадром — одиннадцать
 * отдельных запросов, включая `admin`: тексты админки уезжали каждому гостю на лендинге.
 * Теперь страница просит ровно свой набор, одним запросом, и `admin` не покидает `/admin`.
 *
 * ⚠️ Набор объявляется РЯДОМ С МАРШРУТОМ, а не внутри компонента. Компонент читает каталог
 * синхронно (`c.search.placeholder`), то есть к моменту его рендера namespace обязан уже
 * лежать в сторе; ждать его можно только там, где страница ещё не смонтирована — в
 * ленивом импорте маршрута. Отсюда и форма: карта «страница → namespace», которую
 * router.tsx и HomeRoute разворачивают в `ensureNamespaces()` перед резолвом чанка.
 *
 * ⚠️ Забыли namespace — упадёт СРАЗУ и внятно: в разработке страж каталога бросает
 * «missing key …» на первом же обращении. Молчаливой пустоты в вёрстке не будет.
 */

/**
 * Загружается до первого кадра и есть на любой странице.
 *
 * Два из четырёх попали сюда не по недосмотру, а по расчёту:
 *
 * `landing` — в нём лежит блок `brand`, название и слоган, которые рисует логотип, а
 * логотип стоит почти на каждом экране продукта. Вынести `brand` в `common` было бы
 * честнее, но это переименование ключей в каталоге и во всех вызовах — отдельная правка.
 *
 * `support` — из него футер лендинга берёт названия соцсетей, а кабинет — модалку
 * «Սոց․ ցանցեր». Держать его отдельным добором значило бы делать НА ЛЕНДИНГЕ второй
 * запрос ради 398 байт; в ядре он обходится дешевле одного лишнего round-trip.
 */
export const CORE_NAMESPACES: readonly Namespace[] = ['common', 'errors', 'landing', 'support'];

/**
 * Дополнения к ядру. Ключ — страница, значение — что ей нужно СВЕРХ CORE_NAMESPACES.
 *
 * Страницы контактов здесь нет намеренно: ей хватает ядра, и вызов `ensureNamespaces([])`
 * только делал бы вид, что что-то грузится.
 */
export const PAGE_NAMESPACES = {
  /**
   * ⚠️ Лендинг добирает `payment` — редизайн вывел на него секцию тарифов, а слово «դրամ»
   * и подпись кнопки оплаты живут там же, откуда их берёт модалка кабинета. Это цена
   * одного дополнительного namespace в запросе `bundle` на самой аудируемой странице
   * продукта; дублировать те же строки во второй namespace было бы дороже — одинаковые
   * надписи в двух местах расходятся при первой правке.
   */
  landing: ['payment'],
  /** Кабинет: профиль, поиск и модалка тарифов. */
  dashboard: ['account', 'search', 'payment'],
  /** Вход, регистрация, сброс пароля, результат подтверждения почты. */
  auth: ['auth'],
  legal: ['legal'],
  /** Экран возврата из платёжного шлюза называет тариф и число дней. */
  paymentResult: ['account', 'payment'],
  admin: ['admin'],
} as const satisfies Record<string, readonly Namespace[]>;
